import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MessageSquare, PhoneCall, Radio } from "lucide-react";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";

import UrgentCallModal from "@/components/alerts/UrgentCallModal.jsx";
import { showAlertToast } from "@/components/alerts/AlertToast.jsx";
import CallPopup from "@/calling/components/CallPopup.jsx";
import StaffChatDrawer from "@/components/chat/StaffChatDrawer.jsx";
import { notificationsApi } from "@/api/notifications/notificationsApi.js";
import { HotelAlertContext } from "@/hooks/useHotelAlerts.js";
import { CALL_STATES } from "@/calling/callConstants.js";
import { getGlobalCallRingtone } from "@/calling/realtime/callRingtone.js";
import { useReceptionCallDesk } from "@/hooks/useReceptionCallDesk.js";
import { cn } from "@/lib/utils";
import {
  ALERT_EVENT_NAME,
  ALERT_PRIORITIES,
  HOTEL_DEPARTMENTS,
  isAllDepartment,
  normalizeDepartment,
  normalizeDepartmentCallAlert,
  normalizePushAlert,
  normalizeRequestAlert,
  playDepartmentAlertSound,
  showBrowserAlertNotification,
} from "@/utils/alerts/departmentAlerts.js";
import { getNotificationPreference } from "@/utils/notifications/pushNotifications.js";
import {
  getRealtimeHubConnection,
  startRealtimeHubConnection,
} from "@/utils/realtimeHub.js";
import { REQUEST_STATUS } from "@/utils/constants.js";
import { HOTEL_PERMISSIONS as P } from "@/constants/hotelPermissions.js";

const MAX_ALERTS = 50;

// Single hotel alert owner: SignalR events, service-worker messages, dedupe,
// ringing, urgent modals, and alert-center insertion should flow through here.
const getField = (item = {}, lowerName, upperName) => item?.[lowerName] || item?.[upperName] || "";

const getAlertKey = (alert = {}) => {
  const reminderSuffix =
    alert.isReminder || (alert.reminderCount && Number(alert.reminderCount) > 0)
      ? `:reminder:${alert.reminderCount || 1}`
      : "";

  return String(
    (alert.notificationEventId || alert.NotificationEventId)
      ? `${alert.notificationEventId || alert.NotificationEventId}${reminderSuffix}`
      : alert.sourceKey ||
        alert.SourceKey ||
        (alert.sourceType && alert.sourceId ? `${alert.sourceType}:${alert.sourceId}${reminderSuffix}` : "") ||
        (alert.callId ? `call:${alert.callId}` : "") ||
        (alert.requestId ? `request:${alert.requestId}${reminderSuffix}` : "") ||
        (alert.orderId ? `order:${alert.orderId}${reminderSuffix}` : "") ||
        alert.id ||
        `${alert.sourceType || "alert"}:${alert.createdAt || Date.now()}${reminderSuffix}`,
  );
};

const isOrderAlertPayload = (payload = {}) => {
  const data = payload.data || payload.Data || {};
  const sourceType = String(
    payload.sourceType ||
      payload.SourceType ||
      data.sourceType ||
      data.SourceType ||
      "",
  )
    .trim()
    .toLowerCase();

  return sourceType === "order" || sourceType === "posorder";
};

const normalizeRequestStatus = (payload = {}) =>
  getField(payload, "statusName", "StatusName") ||
  getField(payload, "status", "Status") ||
  "";

const isPendingRequestPayload = (payload = {}) =>
  String(normalizeRequestStatus(payload)).toLowerCase() === String(REQUEST_STATUS.PENDING).toLowerCase();

const extractCallId = (detail = {}) =>
  detail.callId ||
  detail.CallId ||
  detail.payload?.callId ||
  detail.payload?.CallId ||
  detail.payload?.data?.callId ||
  detail.payload?.Data?.CallId ||
  "";

const getAckPayload = (payload = {}, status) => {
  const data = payload.data || payload.Data || {};
  const deliveryId = payload.deliveryId || payload.DeliveryId || data.deliveryId || data.DeliveryId;
  const notificationEventId =
    payload.notificationEventId ||
    payload.NotificationEventId ||
    data.notificationEventId ||
    data.NotificationEventId;
  const ackToken = payload.ackToken || payload.AckToken || data.ackToken || data.AckToken;

  if (!deliveryId || !notificationEventId || !ackToken) return null;

  return {
    deliveryId,
    notificationEventId,
    ackToken,
    status,
    sourceType: payload.sourceType || payload.SourceType || data.sourceType || data.SourceType,
    sourceId: payload.sourceId || payload.SourceId || data.sourceId || data.SourceId,
    clientUrl: window.location.href,
  };
};

const acknowledgeDelivery = (payload = {}, status) => {
  const ackPayload = getAckPayload(payload, status);
  if (!ackPayload) return;

  notificationsApi.acknowledgeDelivery(ackPayload).catch(() => {});
};

const useRepeatingAlertSound = () => {
  const repeatTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);

  const stopRepeating = useCallback(() => {
    if (repeatTimerRef.current) {
      window.clearInterval(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      window.clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const startRepeating = useCallback((enabled) => {
    stopRepeating();
    if (enabled) {
      repeatTimerRef.current = window.setInterval(playDepartmentAlertSound, 2400);
    }
    timeoutTimerRef.current = window.setTimeout(stopRepeating, 60000);
  }, [stopRepeating]);

  useEffect(() => stopRepeating, [stopRepeating]);

  return { startRepeating, stopRepeating };
};

const HotelCallDeskRuntime = ({ authContext, onCallApi, onIncomingCallAlert, onLookupResult }) => {
  const remoteAudioRef = useRef(null);
  const call = useReceptionCallDesk({ remoteAudioRef, authContext, onIncomingCallAlert });
  const isBusy = call.callState !== CALL_STATES.idle;
  const isOnline = call.status === "connected" && call.registered;
  const [viewMode, setViewMode] = useState("pill"); // 'expanded' | 'pill' | 'circle'
  const [timedOut, setTimedOut] = useState(false);

  // Avoid perpetual spinner if signaling takes time
  useEffect(() => {
    if (!isOnline && call.status !== "failed") {
      const timer = setTimeout(() => setTimedOut(true), 6000);
      return () => clearTimeout(timer);
    } else {
      setTimedOut(false);
    }
  }, [isOnline, call.status]);

  // Auto-minimize to pill after online confirmation
  useEffect(() => {
    if (isOnline) {
      const timer = setTimeout(() => {
        setViewMode((current) => (current === "expanded" ? "pill" : current));
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  useEffect(() => {
    onCallApi(call);
  }, [call, onCallApi]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("hotel:guestPresenceSnapshot", {
      detail: { tokens: Array.from(call.callableGuestSessions) },
    }));
  }, [call.callableGuestSessions]);

  useEffect(() => {
    const handleLookupResult = (event) => onLookupResult(event.detail || {});
    window.addEventListener("hotel:incomingCallLookupResult", handleLookupResult);
    return () => {
      window.removeEventListener("hotel:incomingCallLookupResult", handleLookupResult);
    };
  }, [onLookupResult]);

  const title = isOnline
    ? "Call desk online"
    : call.status === "reconnecting"
      ? "Reconnecting call desk"
      : call.status === "failed"
        ? "Call desk offline"
        : timedOut
          ? "Call desk standby"
          : "Preparing call desk";

  const description = isOnline
    ? "Ready for guest calls"
    : call.status === "reconnecting"
      ? "Restoring reception calls..."
      : call.status === "failed"
        ? "Unable to register reception calls"
        : timedOut
          ? "Listening for incoming calls in background"
          : "Setting up reception calls...";

  const [isChatOpen, setIsChatOpen] = useState(false);

  return (
    <>
      {!isBusy && (
        <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2">
          {/* Quick Chat Messenger Button */}
          <button
            type="button"
            onClick={() => setIsChatOpen(true)}
            className="group relative flex h-11 w-11 items-center justify-center rounded-full border border-slate-200/90 bg-white/95 text-slate-800 shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:border-slate-300 hover:bg-slate-50 hover:shadow-2xl"
            title="Open Hotel Chat & Messages"
          >
            <MessageSquare className="h-5 w-5 text-primary transition-transform duration-200 group-hover:scale-110" />
          </button>

          {viewMode === "circle" ? (
            <button
              type="button"
              onClick={() => setViewMode("pill")}
              className="group relative flex h-11 w-11 items-center justify-center rounded-full border border-slate-200/90 bg-white/95 text-slate-800 shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:border-slate-300 hover:bg-slate-50 hover:shadow-2xl"
              title="Open Call Desk"
            >
              <PhoneCall
                className={cn(
                  "h-5 w-5 transition-transform duration-200 group-hover:rotate-12",
                  isOnline ? "text-emerald-600" : timedOut ? "text-sky-600" : "text-amber-600"
                )}
              />
              <span
                className={cn(
                  "absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white",
                  isOnline
                    ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"
                    : timedOut
                      ? "bg-sky-500"
                      : "bg-amber-500 animate-pulse"
                )}
              />
            </button>
          ) : viewMode === "pill" ? (
            <div className={cn(
              "group flex items-center rounded-full border bg-white/95 pl-3 pr-1.5 py-1 text-xs font-semibold shadow-md backdrop-blur-md transition-all hover:bg-slate-50 hover:shadow-lg",
              call.waitingCall ? "border-amber-400 bg-amber-50/95 ring-2 ring-amber-400/40 animate-bounce" : "border-slate-200/90 text-slate-700 hover:border-slate-300"
            )}>
              <button
                type="button"
                onClick={() => setViewMode("expanded")}
                className="flex items-center gap-2 py-0.5"
                title="Click to view reception call desk options"
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full transition-colors",
                    call.waitingCall
                      ? "bg-amber-500 animate-ping"
                      : isOnline
                        ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"
                        : timedOut
                          ? "bg-sky-500"
                          : "bg-amber-500 animate-pulse",
                  )}
                />
                <span className={cn("text-[11px] font-bold", call.waitingCall ? "text-amber-950" : "text-slate-800")}>
                  {call.waitingCall ? "⚠️ Call Waiting!" : isOnline ? "Call Desk Ready" : timedOut ? "Call Desk Standby" : "Connecting..."}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("circle")}
                className="ml-1.5 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                title="Minimize to circle"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="relative rounded-2xl border border-slate-200/90 bg-white/95 p-3.5 shadow-2xl backdrop-blur-md max-w-xs transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      isOnline
                        ? "bg-emerald-100 text-emerald-700"
                        : timedOut
                          ? "bg-sky-100 text-sky-700"
                          : "bg-amber-100 text-amber-700",
                    )}
                  >
                    {isOnline ? (
                      <Radio className="h-4 w-4" />
                    ) : timedOut ? (
                      <Radio className="h-4 w-4 text-sky-600" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-950 truncate">{title}</p>
                    <p className="text-[11px] text-slate-500 leading-tight">{description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("pill")}
                    className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Minimize to pill"
                  >
                    _
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("circle")}
                    className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Minimize to circle"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Direct Quick Action Links */}
              <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-2.5">
                <Link
                  to="/hotel/calls/directory"
                  className="flex h-8 flex-1 items-center justify-center rounded-xl bg-primary px-3 text-xs font-bold text-white shadow-2xs transition-all hover:bg-primary/90"
                >
                  Open Call Desk →
                </Link>
                <button
                  type="button"
                  onClick={() => setIsChatOpen(true)}
                  className="flex h-8 items-center justify-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-2.5 text-xs font-semibold text-slate-700 shadow-2xs transition-all hover:bg-slate-100"
                >
                  <MessageSquare className="h-3.5 w-3.5 text-primary" />
                  <span>Chat</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <StaffChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        authContext={authContext}
        onStartCall={(conv) => {
          setIsChatOpen(false);
          if (conv.guestSessionId) {
            call.startReceptionCall({ sessionId: conv.guestSessionId, guestName: conv.guestName, roomName: conv.roomName });
          } else if (conv.participant1UserId && conv.participant1UserId !== authContext?.userId) {
            call.startStaffCall({ userId: conv.participant1UserId });
          } else if (conv.participant2UserId && conv.participant2UserId !== authContext?.userId) {
            call.startStaffCall({ userId: conv.participant2UserId });
          }
        }}
      />
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <CallPopup
        callState={call.callState}
        callInfo={call.callInfo}
        waitingCall={call.waitingCall}
        heldCall={call.heldCall}
        muted={call.muted}
        isHeld={call.isHeld}
        isRemoteHeld={call.isRemoteHeld}
        isTransferring={call.isTransferring}
        transferringDepartment={call.transferringDepartment}
        callDuration={call.callDuration}
        onAccept={call.acceptCall}
        onHoldAndAnswerWaiting={call.holdAndAnswerWaiting}
        onSwapActiveLine={call.swapActiveLine}
        onDismissWaitingCall={call.dismissWaitingCall}
        onDecline={call.declineCall}
        onEnd={call.endCall}
        onToggleMute={call.toggleMute}
        onHoldCall={call.holdCall}
        onTransferCall={call.transferCall}
      />
    </>
  );
};

const HotelAlertProvider = ({
  children,
  authContext,
  onHubConnectedChange,
  onPendingAlert,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const callDeskRef = useRef(null);
  const pendingIncomingOpenRef = useRef(null);
  const recentlyPresentedRef = useRef(new Map());
  const departmentSyncVersionRef = useRef(0);
  const [alerts, setAlerts] = useState([]);
  const [activeUrgentAlert, setActiveUrgentAlert] = useState(null);
  const [incomingCallLookup, setIncomingCallLookup] = useState(null);
  const [currentDepartment, setCurrentDepartment] = useState(() =>
    normalizeDepartment(localStorage.getItem("hotelDepartmentFilter"), HOTEL_DEPARTMENTS.reception),
  );
  const currentDepartmentRef = useRef(currentDepartment);
  const { startRepeating, stopRepeating } = useRepeatingAlertSound();

  const authReady = Boolean(
    authContext?.authReady &&
      authContext?.accessToken &&
      authContext?.userId &&
      authContext?.organizationId,
  );
  const canReceiveCalls = authContext?.hasPermission?.(P.calls.receive) ?? false;
  const canStartCalls = authContext?.hasPermission?.(P.calls.start) ?? false;
  const canStartDepartmentCalls = authContext?.hasPermission?.(P.calls.departmentCall) ?? false;

  useEffect(() => {
    currentDepartmentRef.current = currentDepartment;
  }, [currentDepartment]);

  // Auto-unlock audio context on first user interaction so call sound plays seamlessly
  useEffect(() => {
    const unlockAudio = () => {
      try {
        const silentAudio = new Audio();
        silentAudio.volume = 0.01;
        silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
      } catch (e) {
        console.log("[audio] auto unlock init", e);
      }
    };
    window.addEventListener("click", unlockAudio, { once: true });
    window.addEventListener("touchstart", unlockAudio, { once: true });
    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
  }, []);

  const alertMatchesCurrentDepartment = useCallback((alert = {}) => {
    const activeDepartment = currentDepartmentRef.current;
    if (isAllDepartment(activeDepartment)) return true;

    // Reminders and urgent alerts should always be visible to staff regardless of department filter
    if (
      alert.isReminder ||
      (alert.reminderCount && Number(alert.reminderCount) > 0) ||
      alert.priority === ALERT_PRIORITIES.urgent
    ) {
      return true;
    }

    // Reception / Front desk oversees all requests and alerts
    if (normalizeDepartment(activeDepartment) === HOTEL_DEPARTMENTS.reception) {
      return true;
    }

    const alertDepartment = normalizeDepartment(
      alert.department ||
        alert.Department ||
        alert.targetDepartment ||
        alert.TargetDepartment ||
        activeDepartment,
      activeDepartment,
    );

    return normalizeDepartment(alertDepartment) === normalizeDepartment(activeDepartment);
  }, []);

  const upsertAlert = useCallback((alert) => {
    if (!alert?.id) return;

    setAlerts((current) => {
      const key = getAlertKey(alert);
      const existing = current.find((item) => getAlertKey(item) === key);
      const withoutCurrent = current.filter((item) => getAlertKey(item) !== key);
      return [{ ...(existing || {}), ...alert }, ...withoutCurrent].slice(0, MAX_ALERTS);
    });
  }, []);

  const handleAlertPresentation = useCallback((alert, options = {}) => {
    if (!alert?.id) return;
    if (!options.ignoreDepartmentFilter && !alertMatchesCurrentDepartment(alert)) return;

    const key = getAlertKey(alert);
    const now = Date.now();
    const lastPresentedAt = recentlyPresentedRef.current.get(key);
    const shouldPresent = !lastPresentedAt || now - lastPresentedAt > 8000 || options.forcePresentation;

    upsertAlert(alert);
    window.dispatchEvent(new CustomEvent(ALERT_EVENT_NAME, {
      detail: { ...alert, __fromHotelAlertProvider: true },
    }));

    if (!shouldPresent) return;

    recentlyPresentedRef.current.set(key, now);
    recentlyPresentedRef.current.forEach((value, recentKey) => {
      if (now - value > 60000) recentlyPresentedRef.current.delete(recentKey);
    });

    showAlertToast(alert);
    showBrowserAlertNotification(alert);

    const soundEnabled = getNotificationPreference("hotelDepartmentAlertSound", true);
    const vibrationEnabled = getNotificationPreference("hotelDepartmentAlertVibration", true);
    const urgentModalEnabled = getNotificationPreference("hotelDepartmentUrgentModal", true);
    const isCallAlert =
      String(alert.sourceType || "").toLowerCase().includes("call") ||
      String(alert.requestType || "").toLowerCase().includes("call") ||
      String(alert.type || "").toLowerCase().includes("call");

    const isUrgent =
      alert.priority === ALERT_PRIORITIES.urgent ||
      alert.priority === ALERT_PRIORITIES.high ||
      isCallAlert;

    if (vibrationEnabled && navigator.vibrate && isUrgent) {
      navigator.vibrate([220, 90, 220, 90, 320]);
    }

    if (soundEnabled && !isCallAlert) {
      playDepartmentAlertSound();
    }

    if (isUrgent && urgentModalEnabled && !isCallAlert) {
      setActiveUrgentAlert(alert);
      startRepeating(soundEnabled);
    }
  }, [alertMatchesCurrentDepartment, startRepeating, upsertAlert]);

  const ingestAlert = useCallback((alert, options = {}) => {
    if (!alert?.id) return;
    handleAlertPresentation(alert, options);
  }, [handleAlertPresentation]);

  const openIncomingCall = useCallback((detail = {}) => {
    const callId = extractCallId(detail);
    if (!callId) return;

    const targetRoute = `/hotel/calls/incoming/${callId}`;
    if (location.pathname !== targetRoute) {
      navigate(targetRoute);
    }

    const openPayload = {
      ...detail,
      callId,
      payload: detail.payload || detail,
    };

    if (callDeskRef.current?.openIncomingCall) {
      callDeskRef.current.openIncomingCall(openPayload);
      return;
    }

    pendingIncomingOpenRef.current = openPayload;
  }, [location.pathname, navigate]);

  const handleServiceWorkerPayload = useCallback((payload = {}, messageType = "") => {
    if (!payload || typeof payload !== "object") return;

    // Check if notification was triggered by current user
    const currentUserId = authContext?.userId || authContext?.user?.id;
    const senderUserId = payload.data?.senderUserId || payload.senderUserId;
    if (currentUserId && senderUserId && String(senderUserId).toLowerCase() === String(currentUserId).toLowerCase()) {
      return;
    }

    // Suppress chat message toasts from push since useChatSession handles real-time SignalR chat toasts
    const eventType = payload.eventType || payload.EventType || payload.data?.eventType;
    if (eventType === "ChatMessage" || String(payload.sourceType || payload.data?.sourceType).toLowerCase() === "chat") {
      acknowledgeDelivery(payload, "ReceivedInApp");
      return;
    }

    acknowledgeDelivery(payload, messageType === "OPEN_INCOMING_CALL" ? "OpenedInApp" : "ReceivedInApp");
    const alert = normalizePushAlert(payload);
    const isReminder =
      eventType === "guest.request.reminder" ||
      payload.data?.eventType === "guest.request.reminder" ||
      Boolean(payload.data?.reminderCount && Number(payload.data.reminderCount) > 0) ||
      Boolean(payload.isReminder || payload.data?.isReminder === "True" || payload.data?.isReminder === true);

    const alertToIngest = isReminder
      ? {
          ...alert,
          isReminder: true,
          priority: ALERT_PRIORITIES.high,
          title: alert.title && alert.title.toLowerCase().startsWith("reminder")
            ? alert.title
            : `Reminder: ${alert.title || "Guest request"}`,
        }
      : alert;

    ingestAlert(alertToIngest, { forcePresentation: true });

    if (messageType === "OPEN_INCOMING_CALL" || String(alert.sourceType).toLowerCase() === "call") {
      openIncomingCall({
        callId: extractCallId({ payload }) || alert.sourceId,
        action: payload.action || payload.Action || "open",
        payload,
      });
    }
  }, [authContext, ingestAlert, openIncomingCall]);

  const acknowledgeAlert = useCallback((alertId) => {
    setAlerts((current) => current.map((alert) =>
      getAlertKey(alert) === String(alertId) ? { ...alert, acknowledged: true } : alert,
    ));
  }, []);

  const dismissAlert = useCallback((alertId) => {
    setAlerts((current) => current.filter((alert) => getAlertKey(alert) !== String(alertId)));
    if (activeUrgentAlert && getAlertKey(activeUrgentAlert) === String(alertId)) {
      stopRepeating();
      setActiveUrgentAlert(null);
    }
  }, [activeUrgentAlert, stopRepeating]);

  const openAlert = useCallback((alert) => {
    if (!alert?.route) return;
    navigate(alert.route);
  }, [navigate]);

  const setDepartment = useCallback((department, options = {}) => {
    if (!department) return;

    const nextDepartment = normalizeDepartment(department, HOTEL_DEPARTMENTS.reception);
    const previousDepartment = currentDepartmentRef.current;
    departmentSyncVersionRef.current += 1;

    currentDepartmentRef.current = nextDepartment;
    setCurrentDepartment(nextDepartment);
    localStorage.setItem("hotelDepartmentFilter", nextDepartment);

    if (!isAllDepartment(nextDepartment)) {
      setAlerts((current) => current.filter((alert) => alertMatchesCurrentDepartment(alert)));
      setActiveUrgentAlert((current) => {
        if (!current || alertMatchesCurrentDepartment(current)) return current;
        stopRepeating();
        return null;
      });
    }

    window.dispatchEvent(new CustomEvent("hotel:departmentChanged", {
      detail: {
        department: nextDepartment,
        previousDepartment,
        version: departmentSyncVersionRef.current,
        source: "HotelAlertProvider",
        initiator: options.source || "HotelAlertProvider",
      },
    }));
  }, [alertMatchesCurrentDepartment, stopRepeating]);

  useEffect(() => {
    const handleExternalDepartmentChange = (event) => {
      if (event.detail?.source === "HotelAlertProvider") return;
      const nextDepartment = event.detail?.department;
      if (nextDepartment && normalizeDepartment(nextDepartment) === normalizeDepartment(currentDepartmentRef.current)) {
        return;
      }
      if (nextDepartment) {
        setDepartment(nextDepartment, { source: "external" });
      }
    };

    window.addEventListener("hotel:departmentChanged", handleExternalDepartmentChange);
    return () => {
      window.removeEventListener("hotel:departmentChanged", handleExternalDepartmentChange);
    };
  }, [setDepartment]);

  const acceptCall = useCallback(() => {
    stopRepeating();
    getGlobalCallRingtone().stop();
    callDeskRef.current?.acceptCall?.();
  }, [stopRepeating]);

  const declineCall = useCallback(() => {
    stopRepeating();
    getGlobalCallRingtone().stop();
    callDeskRef.current?.declineCall?.();
  }, [stopRepeating]);

  const startReceptionCall = useCallback((session) => {
    if (!canStartCalls && !canStartDepartmentCalls) {
      toast.error("You do not have permission to place calls.");
      return Promise.reject(new Error("Permission denied"));
    }
    if (!callDeskRef.current?.startReceptionCall) {
      toast.error("Call service is connecting. Please try again in a moment.");
      return Promise.reject(new Error("Call desk offline"));
    }
    return callDeskRef.current.startReceptionCall(session);
  }, [canStartCalls, canStartDepartmentCalls]);

  const startDepartmentCall = useCallback((department) => {
    if (!canStartDepartmentCalls && !canStartCalls) {
      toast.error("You do not have permission to place department calls.");
      return Promise.reject(new Error("Permission denied"));
    }
    if (!callDeskRef.current?.startDepartmentCall) {
      toast.error("Call service is connecting. Please try again in a moment.");
      return Promise.reject(new Error("Call desk offline"));
    }
    return callDeskRef.current.startDepartmentCall(department);
  }, [canStartCalls, canStartDepartmentCalls]);

  const startStaffCall = useCallback((user) => {
    if (!canStartCalls && !canStartDepartmentCalls) {
      toast.error("You do not have permission to call staff members.");
      return Promise.reject(new Error("Permission denied"));
    }
    if (!callDeskRef.current?.startStaffCall) {
      toast.error("Call service is connecting. Please try again in a moment.");
      return Promise.reject(new Error("Call desk offline"));
    }
    return callDeskRef.current.startStaffCall(user);
  }, [canStartCalls, canStartDepartmentCalls]);

  const onCallApi = useCallback((call) => {
    callDeskRef.current = call;
    if (pendingIncomingOpenRef.current && call?.openIncomingCall) {
      const pendingOpen = pendingIncomingOpenRef.current;
      pendingIncomingOpenRef.current = null;
      call.openIncomingCall(pendingOpen);
    }
  }, []);

  const onLookupResult = useCallback((detail = {}) => {
    setIncomingCallLookup(detail);
  }, []);

  const onIncomingCallAlert = useCallback((payload = {}) => {
    ingestAlert(normalizePushAlert({
      ...payload,
      sourceType: "call",
      type: "INCOMING_CALL",
    }), { forcePresentation: true });
  }, [ingestAlert]);

  useEffect(() => {
    const handleAlert = (event) => {
      if (event.detail?.__fromHotelAlertProvider) return;
      handleAlertPresentation(event.detail);
    };
    window.addEventListener(ALERT_EVENT_NAME, handleAlert);
    return () => {
      window.removeEventListener(ALERT_EVENT_NAME, handleAlert);
      stopRepeating();
    };
  }, [handleAlertPresentation, stopRepeating]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return undefined;

    const handleServiceWorkerMessage = (event) => {
      const type = event.data?.type;
      const payload = event.data?.payload || {};

      if (type === "HOTEL_PUSH_NOTIFICATION") {
        handleServiceWorkerPayload(payload, type);
        return;
      }

      if (type === "OPEN_INCOMING_CALL") {
        handleServiceWorkerPayload({
          ...payload,
          action: event.data?.action || "open",
          callId: event.data?.callId || payload.callId || payload?.data?.callId,
        }, type);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [handleServiceWorkerPayload]);

  useEffect(() => {
    if (!authReady) {
      onHubConnectedChange?.(false);
      return undefined;
    }

    let connection;
    try {
      connection = getRealtimeHubConnection();
    } catch {
      onHubConnectedChange?.(false);
      return undefined;
    }

    const handleGuestRequestCreated = (payload = {}) => {
      if (isOrderAlertPayload(payload)) {
        ingestAlert(normalizePushAlert(payload), { forcePresentation: true });
        return;
      }

      if (isPendingRequestPayload(payload)) {
        onPendingAlert?.(1);
      }

      ingestAlert(normalizeRequestAlert(payload), { forcePresentation: true });
    };

    const handleGuestRequestReminder = (payload = {}) => {
      const isOrder = isOrderAlertPayload(payload);
      const baseAlert = isOrder ? normalizePushAlert(payload) : normalizeRequestAlert(payload);
      const reminderCount = payload.reminderCount || payload.ReminderCount || baseAlert.reminderCount || 1;
      const room = payload.roomName || payload.RoomName || payload.roomNumber || payload.RoomNumber || baseAlert.roomNumber;
      const roomDisplay = room && room !== "—" ? `Room ${room}` : "";

      const reminderAlert = {
        ...baseAlert,
        id: `request-reminder-${baseAlert.sourceId || payload.id || Date.now()}-${reminderCount}`,
        isReminder: true,
        priority: ALERT_PRIORITIES.high,
        title: `Reminder #${reminderCount}: ${baseAlert.title || "Guest request"}`,
        description: payload.message || payload.description || `${roomDisplay ? `${roomDisplay}: ` : ""}Guest asked for a quick follow-up on this request.`,
        reminderCount,
      };

      ingestAlert(reminderAlert, { forcePresentation: true });
    };

    const handleGuestRequestChanged = (payload = {}) => {
      ingestAlert(isOrderAlertPayload(payload) ? normalizePushAlert(payload) : normalizeRequestAlert(payload));
    };

    const handleDepartmentCallCreated = (payload = {}) => {
      ingestAlert(normalizeDepartmentCallAlert(payload));
    };

    const handleChatMessage = (incomingMsg) => {
      if (!incomingMsg) return;
      const myId = authContext?.userId;
      const isSenderMe =
        (incomingMsg.senderUserId && myId && String(incomingMsg.senderUserId).toLowerCase() === String(myId).toLowerCase()) ||
        (incomingMsg.SenderUserId && myId && String(incomingMsg.SenderUserId).toLowerCase() === String(myId).toLowerCase());

      if (isSenderMe) return;

      const senderDisplayName =
        incomingMsg.senderDisplayName ||
        incomingMsg.SenderDisplayName ||
        (incomingMsg.senderType === 'Guest' || incomingMsg.SenderType === 'Guest'
          ? (incomingMsg.roomName || incomingMsg.RoomName ? `Room ${incomingMsg.roomName || incomingMsg.RoomName}` : 'Guest')
          : 'Hotel Staff');
      const text = incomingMsg.content || incomingMsg.Content || 'New message received';

      if (typeof window !== 'undefined' && window.Notification?.permission === 'granted' && document.visibilityState !== 'visible') {
        try {
          const n = new Notification(`💬 ${senderDisplayName}`, {
            body: text,
            icon: '/pwa-icon.svg',
            tag: `chat-${incomingMsg.conversationId || incomingMsg.ConversationId || 'chat'}`,
          });
          n.onclick = () => {
            window.focus();
            window.dispatchEvent(new CustomEvent('hotel:openChatDrawer', { detail: incomingMsg }));
          };
        } catch {}
      }

      const soundEnabled = getNotificationPreference('hotelDepartmentAlertSound', true);
      if (soundEnabled) {
        playDepartmentAlertSound();
      }

      window.dispatchEvent(new CustomEvent('hotel:chatMessageReceived', { detail: incomingMsg }));
    };

    const handleOpenIncomingCall = (event) => {
      openIncomingCall(event.detail || {});
    };

    const handleStartReceptionCall = (event) => {
      startReceptionCall(event.detail?.session);
    };

    const handleStartDepartmentCall = (event) => {
      startDepartmentCall(event.detail || event.detail?.department);
    };

    connection.on("GuestRequestCreated", handleGuestRequestCreated);
    connection.on("GuestRequestReminder", handleGuestRequestReminder);
    connection.on("GuestRequestStatusChanged", handleGuestRequestChanged);
    connection.on("GuestRequestAssigned", handleGuestRequestChanged);
    connection.on("DepartmentCallCreated", handleDepartmentCallCreated);
    connection.on("ReceiveChatMessage", handleChatMessage);
    connection.on("receivechatmessage", handleChatMessage);

    window.addEventListener("hotel:openIncomingCall", handleOpenIncomingCall);
    window.addEventListener("hotel:startReceptionCall", handleStartReceptionCall);
    window.addEventListener("hotel:startDepartmentCall", handleStartDepartmentCall);

    startRealtimeHubConnection()
      .then(() => onHubConnectedChange?.(true))
      .catch(() => onHubConnectedChange?.(false));

    if (!connection.__hotelAlertProviderLifecycleRegistered) {
      connection.__hotelAlertProviderLifecycleRegistered = true;
      connection.onreconnected(() => {
        onHubConnectedChange?.(true);
        window.dispatchEvent(new CustomEvent("hotel:dashboardHubConnected"));
      });
      connection.onclose(() => {
        onHubConnectedChange?.(false);
        window.dispatchEvent(new CustomEvent("hotel:dashboardHubDisconnected"));
      });
    }

    return () => {
      connection.off("GuestRequestCreated", handleGuestRequestCreated);
      connection.off("GuestRequestReminder", handleGuestRequestReminder);
      connection.off("GuestRequestStatusChanged", handleGuestRequestChanged);
      connection.off("GuestRequestAssigned", handleGuestRequestChanged);
      connection.off("DepartmentCallCreated", handleDepartmentCallCreated);
      connection.off("ReceiveChatMessage", handleChatMessage);
      connection.off("receivechatmessage", handleChatMessage);
      window.removeEventListener("hotel:openIncomingCall", handleOpenIncomingCall);
      window.removeEventListener("hotel:startReceptionCall", handleStartReceptionCall);
      window.removeEventListener("hotel:startDepartmentCall", handleStartDepartmentCall);
    };
  }, [
    authReady,
    authContext?.accessToken,
    authContext?.organizationId,
    ingestAlert,
    onHubConnectedChange,
    onPendingAlert,
    openIncomingCall,
    startDepartmentCall,
    startReceptionCall,
  ]);

  const holdCall = useCallback((isHeld) => {
    return callDeskRef.current?.holdCall?.(isHeld);
  }, []);

  const transferCall = useCallback((targetDepartment, targetDepartmentName) => {
    return callDeskRef.current?.transferCall?.(targetDepartment, targetDepartmentName);
  }, []);

  const value = useMemo(() => ({
    alerts,
    activeIncomingCall: incomingCallLookup?.call || null,
    activeUrgentAlert,
    currentDepartment,
    incomingCallLookup,
    isRinging: Boolean(activeUrgentAlert),
    acknowledgeAlert,
    dismissAlert,
    acceptCall,
    declineCall,
    holdCall,
    transferCall,
    openAlert,
    openIncomingCall,
    setDepartment,
    startReceptionCall,
    startDepartmentCall,
    startStaffCall,
  }), [
    acknowledgeAlert,
    activeUrgentAlert,
    alerts,
    acceptCall,
    currentDepartment,
    declineCall,
    dismissAlert,
    holdCall,
    transferCall,
    incomingCallLookup,
    openAlert,
    openIncomingCall,
    setDepartment,
    startDepartmentCall,
    startReceptionCall,
    startStaffCall,
  ]);

  const canAnyCall = canReceiveCalls || canStartCalls || canStartDepartmentCalls;

  return (
    <HotelAlertContext.Provider value={value}>
      {authReady && canAnyCall ? (
        <HotelCallDeskRuntime
          authContext={authContext}
          onCallApi={onCallApi}
          onIncomingCallAlert={onIncomingCallAlert}
          onLookupResult={onLookupResult}
        />
      ) : null}

      {children}

      <UrgentCallModal
        alert={activeUrgentAlert}
        onClose={() => {
          stopRepeating();
          setActiveUrgentAlert(null);
        }}
      />
    </HotelAlertContext.Provider>
  );
};

export default HotelAlertProvider;
